import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import { RootState, store } from "../../utils/store";
import { Spinner } from "../../utils/Spinner";
import { Button, Icon } from "../../components/tailwind";
import { Option } from "../../components/tailwind/dropdown/Dropdown";
import { ImageCarousel } from "./ImageCarousel";
import { useGetProductQuery } from "../../api/productDetailSlice";
import { CustomerFieldEditModal } from "../../customers/customer-fields/CustomerFieldEditModal";
import { MultiFileDrop } from "../../components/FileDrop";
import {
  useEnrichFromFieldsetMutation,
  useEnrichOneMutation,
  useGetProductEnrichViewDataQuery,
  useGetEnrichResultsQuery,
  useGetEnrichValidationQuery,
  useUpdateEnrichResultFieldMutation,
  useRevertEnrichResultFieldMutation,
  useDeleteProductImagesMutation,
  useAddProductImagesMutation,
  useUploadProductFilesMutation,
  useDeleteProductFileMutation,
  useUpdateEnrichInputOverrideMutation,
  useSendEnrichToPimMutation,
  useUpdateMetadataFromEnrichMutation,
  useResyncFromPimMutation,
  useRunPreStepMutation,
  EnrichedField,
  EnrichStatus,
  PreStepContext,
  PreStepSource,
  PreStepTaskResult,
} from "../../api/enrichApi";
import { ValidatedFieldsetField } from "../../api/fieldsetApi";
import { Job } from "../../customers/jobs/types";
import { JobProgressbarHandler } from "../../customers/jobs/JobProgressbarHandler";
import { SSEClient } from "../../api/SSEApi";
import {
  NotificationAppearance,
  setDjangoToastOpen,
} from "../../api/djangoToastSlice";
import { validateFileExtension } from "../../utils/files";
import { TaskQueue } from "../../api/taskQueueApi";
import { useCreateCustomerFieldMutation } from "../../api/customerFieldApi";
import { showMessage } from "../../utils/partialUtils";
import { validateImageUrl } from "../copy-assistant/utils";
import {
  formatTimestamp,
  hasEnrichPimValue,
  hasExistingPimValue,
  wouldOverwriteEnrichPimValue,
} from "./enrichUtils";
import { EnrichFieldsetSection } from "./EnrichFieldsetSection";
import { EnrichViewHeader } from "./EnrichViewHeader";
import { EnrichFieldTable, LiveJobRevealConfig } from "./EnrichFieldTable";
import { Modal } from "../../components/tailwind/modal/Modal";

const ALLOWED_EXTENSIONS = ["pdf"];

type Props = {
  productId: string;
  settingsUrl: string;
};

type AdditionalImageUrl = {
  id: number;
  url: string;
};

export const ProductEnrichView: React.FC<Props> = ({
  productId,
  settingsUrl,
}) => {
  const token = useSelector((state: RootState) => state.auth.token);

  const { data: product, isLoading: isProductLoading } = useGetProductQuery(
    productId
  );
  const {
    data: pageData,
    isLoading: isDataLoading,
    error: dataError,
    refetch: refetchPageData,
  } = useGetProductEnrichViewDataQuery(productId);

  const {
    data: enrichResults,
    isLoading: isResultLoading,
    refetch: refetchEnrichResult,
  } = useGetEnrichResultsQuery(productId);
  const enrichResultsRef = useRef(enrichResults);
  enrichResultsRef.current = enrichResults;

  const [
    enrichFromFieldset,
    { isLoading: isEnriching },
  ] = useEnrichFromFieldsetMutation();
  const [enrichOne] = useEnrichOneMutation();

  const [updateEnrichResultField] = useUpdateEnrichResultFieldMutation();
  const [revertEnrichResultField] = useRevertEnrichResultFieldMutation();

  const [sendToPim] = useSendEnrichToPimMutation();
  const [isSendingToPim, setIsSendingToPim] = useState(false);
  const overwriteWarningDialogRef = useRef<HTMLDialogElement>(null);
  const [pendingSendFields, setPendingSendFields] = useState<
    EnrichedField[] | null
  >(null);
  const [excludedPendingSendIds, setExcludedPendingSendIds] = useState<
    Set<number>
  >(() => new Set());
  const pendingOverwriteFields = useMemo(() => {
    const f = pendingSendFields ?? [];
    return f.filter(wouldOverwriteEnrichPimValue);
  }, [pendingSendFields]);
  const effectivePendingSendFields = useMemo(() => {
    const f = pendingSendFields ?? [];
    if (excludedPendingSendIds.size === 0) {
      return f;
    }
    return f.filter((x) => !excludedPendingSendIds.has(x.id));
  }, [pendingSendFields, excludedPendingSendIds]);
  const effectivePendingOverwriteFields = useMemo(() => {
    return effectivePendingSendFields.filter(wouldOverwriteEnrichPimValue);
  }, [effectivePendingSendFields]);
  const [
    updateMetadataFromEnrich,
    { isLoading: isUpdatingMetadata },
  ] = useUpdateMetadataFromEnrichMutation();
  const [
    resyncFromPim,
    { isLoading: isResyncingFromPim },
  ] = useResyncFromPimMutation();
  const [runPreStep] = useRunPreStepMutation();
  // The mutation only covers the POST that hands back a task_id. The pre-step
  // itself runs as a Celery task polled by TaskQueue, so the visible loading
  // state has to span that whole wait, not just the mutation.
  const [isRunningPreStep, setIsRunningPreStep] = useState(false);
  const [
    createCustomerField,
    { isLoading: isCreatingField },
  ] = useCreateCustomerFieldMutation();
  const [enrichingFieldIds, setEnrichingFieldIds] = useState<Set<number>>(
    new Set()
  );
  // Typed for the pre-step task result, which is the only task resolved off this
  // queue for its value rather than just its completion.
  const taskQueueRef = useRef(new TaskQueue<PreStepTaskResult>(token ?? ""));

  useEffect(() => {
    taskQueueRef.current.token = token ?? "";
  }, [token]);

  const [deleteProductImages] = useDeleteProductImagesMutation();

  const [uploadProductFiles] = useUploadProductFilesMutation();
  const [deleteProductFile] = useDeleteProductFileMutation();

  const [
    addProductImages,
    { isLoading: isAddingImages },
  ] = useAddProductImagesMutation();

  const [
    updateEnrichInputOverride,
    { isLoading: isSavingInputOverride },
  ] = useUpdateEnrichInputOverrideMutation();

  const [selectedFieldset, setSelectedFieldset] = useState<number | null>(null);
  const [activeJobId, setActiveJobId] = useState<number | null>(null);
  const [activeJobDetailUrl, setActiveJobDetailUrl] = useState<string | null>(
    null
  );
  const [editingFieldId, setEditingFieldId] = useState<number | null>(null);
  const additionalImageUrlIdRef = useRef(0);
  const [additionalImageUrls, setAdditionalImageUrls] = useState<
    AdditionalImageUrl[]
  >([]);
  const [activeImageUrl, setActiveImageUrl] = useState<string | null>(null);
  const [enrichFieldIds, setEnrichFieldIds] = useState<Set<number>>(
    () => new Set()
  );
  const [enrichingJob, setEnrichingJob] = useState<Job | null>(null);
  const [streamIsActive, setStreamIsActive] = useState(false);
  const [
    liveJobReveal,
    setLiveJobReveal,
  ] = useState<LiveJobRevealConfig | null>(null);
  const [editableWebIdentification, setEditableWebIdentification] = useState<
    Record<string, string>
  >({});
  const [editableSupplierText, setEditableSupplierText] = useState("");

  // Set initial fieldset when pageData loads
  useEffect(() => {
    if (pageData?.initial_fieldset_id && !selectedFieldset) {
      setSelectedFieldset(pageData.initial_fieldset_id);
    }
  }, [pageData?.initial_fieldset_id]);

  const handleAddFiles = async (files: File[]): Promise<void> => {
    const validFiles = files.filter((file) =>
      validateFileExtension(file, ALLOWED_EXTENSIONS)
    );
    if (validFiles.length < files.length) {
      showMessage(NotificationAppearance.ERROR, "Only PDF files can be added");
    }
    if (validFiles.length === 0) {
      return;
    }

    try {
      await uploadProductFiles({
        product_id: productId,
        files: validFiles,
      }).unwrap();
    } catch (error) {
      console.error("Failed to upload files:", error);
      showMessage(NotificationAppearance.ERROR, "Failed to upload files");
    }
  };

  const handleRemoveFile = async (fileId: number): Promise<void> => {
    try {
      await deleteProductFile({
        product_id: productId,
        file_id: fileId,
      }).unwrap();
    } catch (error) {
      console.error("Failed to delete file:", error);
      showMessage(NotificationAppearance.ERROR, "Failed to delete file");
    }
  };
  // Auto-resume streaming if there's an active job when page loads
  useEffect(() => {
    if (pageData?.job_is_active && !activeJobId) {
      setActiveJobId(pageData.latest_job_id);
      setActiveJobDetailUrl(pageData.latest_job_url ?? null);
    }
  }, [
    pageData?.latest_job_id,
    pageData?.latest_job_url,
    pageData?.job_is_active,
  ]);

  useEffect(() => {
    if (
      !pageData?.job_is_active ||
      pageData.latest_job_id == null ||
      activeJobId == null ||
      activeJobId !== pageData.latest_job_id
    ) {
      return;
    }
    if (isResultLoading) {
      return;
    }
    const raw = pageData.latest_job_customer_field_ids;
    if (!Array.isArray(raw) || raw.length === 0) {
      return;
    }
    const parsed = raw
      .map((id) => (typeof id === "number" ? id : Number(id)))
      .filter((n) => !Number.isNaN(n));
    if (parsed.length === 0) {
      return;
    }
    setLiveJobReveal((prev) => {
      if (prev?.jobId === activeJobId) {
        return prev;
      }
      const er = enrichResults ?? null;
      const baseline: Record<number, number | null> = {};
      for (const id of parsed) {
        const row = er?.find((r) => r.customer_field_id === id);
        baseline[id] = row?.generation_event_id ?? null;
      }
      return {
        jobId: activeJobId,
        scopedCustomerFieldIds: parsed,
        baselineGenerationEventIdByFieldId: baseline,
      };
    });
  }, [
    activeJobId,
    enrichResults,
    isResultLoading,
    pageData?.job_is_active,
    pageData?.latest_job_id,
    pageData?.latest_job_customer_field_ids,
  ]);

  const previousProductIdRef = useRef(productId);
  useEffect(() => {
    if (previousProductIdRef.current === productId) {
      return;
    }
    previousProductIdRef.current = productId;
    setLiveJobReveal(null);
  }, [productId]);

  useEffect(() => {
    if (!pageData) {
      return;
    }

    setEditableWebIdentification(
      Object.fromEntries(
        Object.entries(pageData.web_identification).map(([key, value]) => [
          key,
          String(value ?? ""),
        ])
      )
    );
    setEditableSupplierText(pageData.supplier_text || "");
  }, [pageData?.web_identification, pageData?.supplier_text]);

  // SSE stream management
  // One pre-step context refetch per job, no matter how many stream frames arrive
  // while the parent task's result is still empty.
  const preStepContextFetchedRef = useRef(false);
  useEffect(() => {
    preStepContextFetchedRef.current = false;
  }, [activeJobId]);

  const startStream = useCallback((): { close: () => void } => {
    if (!activeJobId || !token || streamIsActive) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return { close: (): void => {} };
    }

    const sseClient = new SSEClient<Job>(token);
    setStreamIsActive(true);

    sseClient.connect(
      `/api/_internal/customer/jobs/${activeJobId}/stream/`,
      (data) => {
        setEnrichingJob(data);
        // The pre-step runs synchronously inside the parent task, before it queues
        // any field task, so nothing signals it. The parent's Task result flips
        // from {} to its reports the moment it has queued them, which is the
        // earliest point the pre-step can be done, and it rides along on every
        // frame anyway.
        if (
          !preStepContextFetchedRef.current &&
          Object.keys(data?.last_run_task?.result ?? {}).length > 0
        ) {
          preStepContextFetchedRef.current = true;
          refetchPageData();
        }
        const rawIds = data?.arguments?.customer_field_ids;
        if (Array.isArray(rawIds) && rawIds.length > 0) {
          const parsed = rawIds
            .map((id: unknown) => (typeof id === "number" ? id : Number(id)))
            .filter((n) => !Number.isNaN(n));
          if (parsed.length > 0) {
            setLiveJobReveal((prev) => {
              if (prev) {
                return prev;
              }
              const er = enrichResultsRef.current;
              const baseline: Record<number, number | null> = {};
              for (const id of parsed) {
                const row = er?.find((r) => r.customer_field_id === id);
                baseline[id] = row?.generation_event_id ?? null;
              }
              return {
                jobId: activeJobId,
                scopedCustomerFieldIds: parsed,
                baselineGenerationEventIdByFieldId: baseline,
              };
            });
          }
        }
        refetchEnrichResult();
      },
      () => {
        // On close: cleanup and refetch latest result
        setStreamIsActive(false);
        setActiveJobId(null);
        setActiveJobDetailUrl(null);
        setEnrichingJob(null);
        setLiveJobReveal(null);
        refetchEnrichResult();
        // The status badge and the product context both changed while the job
        // ran, and neither query is invalidated by the enrich mutation itself.
        refetchPageData();
      },
      (error, msg) => {
        // On error: show notification with link to job detail
        setStreamIsActive(false);
        setLiveJobReveal(null);
        store.dispatch(
          setDjangoToastOpen({
            appearance: NotificationAppearance.ERROR,
            content: msg || "Something went wrong with the enrichment stream.",
            additionalContent: `Check Job ${activeJobId} for more information.`,
          })
        );
        // Keep activeJobId so user can see it failed
      }
    );

    return {
      close: (): void => {
        sseClient.close();
        setStreamIsActive(false);
      },
    };
  }, [
    activeJobId,
    token,
    streamIsActive,
    refetchEnrichResult,
    refetchPageData,
  ]);

  useEffect(() => {
    if (!activeJobId || !token) {
      return;
    }
    const { close } = startStream();
    return () => {
      close();
    };
  }, [activeJobId, token]);

  const {
    data: validationFields,
    isFetching: isValidating,
    isError: isValidationError,
    refetch: refetchValidation,
  } = useGetEnrichValidationQuery(
    {
      fieldset_id: selectedFieldset ?? 0,
      product_id: productId,
    },
    {
      skip: !selectedFieldset,
    }
  );
  const validationError = isValidationError
    ? "Failed to validate fieldset"
    : null;
  const allFields = validationFields ?? [];

  const sortedValidationFields = useMemo((): ValidatedFieldsetField[] => {
    const rank = (f: ValidatedFieldsetField): number => {
      if (!f.is_valid_field) return 0;
      if (hasExistingPimValue(f)) return 1;
      return 2;
    };
    return [...allFields].sort((a, b) => rank(a) - rank(b));
  }, [allFields]);

  const handleCreateFieldForValidation = async (
    fieldKey: string
  ): Promise<void> => {
    try {
      await createCustomerField({
        field_id: fieldKey,
        name: fieldKey,
      }).unwrap();
      showMessage(
        NotificationAppearance.SUCCESS,
        `Created PIM Field: ${fieldKey}`
      );
      await refetchValidation();
    } catch {
      showMessage(NotificationAppearance.ERROR, "Failed to create field");
    }
  };

  const handleEditModalSave = (): void => {
    setEditingFieldId(null);
    void refetchValidation();
    refetchEnrichResult();
  };

  const enrichFieldSelectionKey = useMemo(() => {
    const keys = sortedValidationFields
      .map((f) => f.key)
      .sort()
      .join("|");
    return `v:${selectedFieldset ?? 0}:${keys}`;
  }, [selectedFieldset, sortedValidationFields]);

  useEffect(() => {
    const next = new Set<number>();
    for (const f of sortedValidationFields) {
      if (
        f.customer_field_id != null &&
        f.is_valid_field &&
        !hasExistingPimValue(f)
      ) {
        next.add(f.customer_field_id);
      }
    }
    setEnrichFieldIds(next);
  }, [enrichFieldSelectionKey]);

  const enrichSelectableFieldIds = useMemo(
    () =>
      sortedValidationFields
        .filter((f) => f.customer_field_id != null && f.is_valid_field)
        .map((f) => f.customer_field_id),
    [sortedValidationFields]
  );

  const {
    enrichSelectAllChecked,
    enrichSelectAllIndeterminate,
  } = useMemo(() => {
    const ids = enrichSelectableFieldIds;
    const n = ids.length;
    if (n === 0) {
      return {
        enrichSelectAllChecked: false,
        enrichSelectAllIndeterminate: false,
      };
    }
    let selected = 0;
    for (const id of ids) {
      if (enrichFieldIds.has(id)) {
        selected += 1;
      }
    }
    return {
      enrichSelectAllChecked: selected === n,
      enrichSelectAllIndeterminate: selected > 0 && selected < n,
    };
  }, [enrichSelectableFieldIds, enrichFieldIds]);

  const hasSelectedFields = enrichFieldIds.size > 0;
  const selectedEnrichResults = useMemo(
    () =>
      (enrichResults ?? []).filter(
        (r) =>
          r.customer_field_id != null &&
          enrichFieldIds.has(r.customer_field_id) &&
          // Discarded rows hold no value; sending them to PIM or into
          // metadata would write nulls. The backend guards this too.
          !r.applicability_discarded
      ),
    [enrichResults, enrichFieldIds]
  );

  const toggleAllEnrichFields = useCallback(() => {
    setEnrichFieldIds((prev) => {
      const ids = sortedValidationFields
        .filter((f) => f.customer_field_id != null && f.is_valid_field)
        .map((f) => f.customer_field_id);
      const allOn = ids.length > 0 && ids.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allOn) {
        for (const id of ids) {
          next.delete(id);
        }
      } else {
        for (const id of ids) {
          next.add(id);
        }
      }
      return next;
    });
  }, [sortedValidationFields]);

  const handleDeleteImage = async (imageUrl?: string): Promise<void> => {
    const urlToDelete =
      imageUrl ?? activeImageUrl ?? pageData?.images?.[0]?.url ?? null;

    try {
      await deleteProductImages({
        product_id: productId,
        image_urls: [urlToDelete],
      }).unwrap();

      showMessage(
        NotificationAppearance.SUCCESS,
        "Successfully deleted image."
      );
    } catch (error) {
      console.error("Failed to delete image:", error);
      showMessage(
        NotificationAppearance.ERROR,
        "Failed to delete image. Please try again."
      );
    }
  };
  const handleAddImageUrlField = (): void => {
    additionalImageUrlIdRef.current += 1;
    setAdditionalImageUrls((prev) => [
      ...prev,
      { id: additionalImageUrlIdRef.current, url: "" },
    ]);
  };
  const handleRemoveImageUrl = (id: number): void => {
    setAdditionalImageUrls((prev) => prev.filter((item) => item.id !== id));
  };

  const handleImageUrlChange = (id: number, value: string): void => {
    setAdditionalImageUrls((prev) => {
      return prev.map((item) =>
        item.id === id ? { ...item, url: value } : item
      );
    });
  };
  const handleAddImages = async (): Promise<void> => {
    const imageUrls = additionalImageUrls
      .map((item) => item.url.trim())
      .filter(Boolean);

    if (imageUrls.length === 0) {
      showMessage(NotificationAppearance.ERROR, "Add at least one image URL");
      return;
    }

    const validationResults = await Promise.all(
      imageUrls.map((url) => validateImageUrl(url))
    );
    const hasInvalidUrl = validationResults.some((isValid) => !isValid);
    if (hasInvalidUrl) {
      showMessage(
        NotificationAppearance.ERROR,
        "One or more image URLs are invalid or inaccessible"
      );
      return;
    }

    try {
      await addProductImages({
        product_id: productId,
        image_urls: imageUrls,
      }).unwrap();
      setAdditionalImageUrls([]);
      showMessage(NotificationAppearance.SUCCESS, "Image URLs added");
    } catch (error) {
      console.error("Failed to add images:", error);
      showMessage(NotificationAppearance.ERROR, "Failed to add image URLs");
    }
  };

  const toggleEnrichFieldId = useCallback(
    (customerFieldId: number) => {
      if (!enrichSelectableFieldIds.includes(customerFieldId)) {
        return;
      }
      setEnrichFieldIds((prev) => {
        const n = new Set(prev);
        if (n.has(customerFieldId)) {
          n.delete(customerFieldId);
        } else {
          n.add(customerFieldId);
        }
        return n;
      });
    },
    [enrichSelectableFieldIds]
  );

  const handleEnrich = async (): Promise<void> => {
    if (!selectedFieldset) {
      alert("Please select an Input Schema first");
      return;
    }

    const selectableIds = enrichSelectableFieldIds;
    const customer_field_ids = selectableIds.filter((id) =>
      enrichFieldIds.has(id)
    );
    if (customer_field_ids.length === 0) {
      alert("Select at least one field to enrich");
      return;
    }

    try {
      const selectedFieldsetName =
        pageData?.fieldsets.find(([id]) => id === selectedFieldset)?.[1] ??
        undefined;
      const result = await enrichFromFieldset({
        product_id: productId,
        fieldset_name: selectedFieldsetName,
        add_reasoning: true,
        customer_field_ids,
      }).unwrap();

      const baseline: Record<number, number | null> = {};
      for (const id of customer_field_ids) {
        const row = enrichResults?.find((r) => r.customer_field_id === id);
        baseline[id] = row?.generation_event_id ?? null;
      }
      setLiveJobReveal({
        jobId: result.job_id,
        scopedCustomerFieldIds: customer_field_ids,
        baselineGenerationEventIdByFieldId: baseline,
      });
      // Start streaming the job progress
      setActiveJobId(result.job_id);
      setActiveJobDetailUrl(result.job_detail_url);
    } catch (error) {
      console.error("Enrichment failed:", error);
      alert("Enrichment failed. Please try again.");
    }
  };

  const handleSaveField = async (
    enrich_result_id: number,
    fieldValue: any
  ): Promise<void> => {
    await updateEnrichResultField({
      product_id: productId,
      enrich_result_id: enrich_result_id,
      field_value: fieldValue,
    }).unwrap();
  };

  const handleRevertField = async (enrich_result_id: number): Promise<void> => {
    await revertEnrichResultField({
      product_id: productId,
      enrich_result_id: enrich_result_id,
    }).unwrap();
  };

  const handleSendToPim = async (fields?: EnrichedField[]): Promise<void> => {
    const toSend = Array.isArray(fields) ? fields : selectedEnrichResults;
    if (toSend.length === 0) {
      return;
    }
    const overwriteFields = toSend.filter(wouldOverwriteEnrichPimValue);
    if (overwriteFields.length > 0) {
      setPendingSendFields(toSend);
      setExcludedPendingSendIds(new Set());
      overwriteWarningDialogRef.current?.showModal();
      return;
    }
    setIsSendingToPim(true);
    try {
      const response = await sendToPim({
        product_id: productId,
        enrich_result_ids: toSend.map((f) => f.id),
      }).unwrap();
      showMessage(
        NotificationAppearance.SUCCESS,
        response.message || "Results successfully sent to PIM."
      );
    } catch (error) {
      console.error("Failed to send enrich results to PIM:", error);
    } finally {
      setIsSendingToPim(false);
    }
  };

  const handleConfirmSendOverwrite = async (): Promise<void> => {
    const toSend = effectivePendingSendFields;
    overwriteWarningDialogRef.current?.close();
    setPendingSendFields(null);
    setExcludedPendingSendIds(new Set());
    if (toSend.length === 0) {
      return;
    }
    setIsSendingToPim(true);
    try {
      const response = await sendToPim({
        product_id: productId,
        enrich_result_ids: toSend.map((f) => f.id),
      }).unwrap();
      showMessage(
        NotificationAppearance.SUCCESS,
        response.message || "Results successfully sent to PIM."
      );
    } catch (error) {
      console.error("Failed to send enrich results to PIM:", error);
    } finally {
      setIsSendingToPim(false);
    }
  };

  const handleUpdateMetadata = async (
    fields?: EnrichedField[]
  ): Promise<void> => {
    const toUpdate = Array.isArray(fields) ? fields : selectedEnrichResults;
    if (toUpdate.length === 0) {
      return;
    }
    try {
      const response = await updateMetadataFromEnrich({
        product_id: productId,
        enrich_result_ids: toUpdate.map((f) => f.id),
      }).unwrap();
      showMessage(
        NotificationAppearance.SUCCESS,
        response.message || "Metadata updated."
      );
    } catch (error) {
      console.error("Failed to update metadata:", error);
      showMessage(
        NotificationAppearance.ERROR,
        "Failed to update metadata. Please try again."
      );
    }
  };

  const handleResyncFromPim = async (): Promise<void> => {
    try {
      const response = await resyncFromPim({ product_id: productId }).unwrap();
      showMessage(
        NotificationAppearance.SUCCESS,
        response.message || "Product re-synced from PIM."
      );
    } catch (error) {
      console.error("Re-sync from PIM failed:", error);
      showMessage(
        NotificationAppearance.ERROR,
        "Re-sync from PIM failed. Please try again."
      );
    }
  };

  const handleRunPreStep = async (): Promise<void> => {
    setIsRunningPreStep(true);
    try {
      const { task_id } = await runPreStep({ product_id: productId }).unwrap();

      taskQueueRef.current.addTaskId(task_id);
      // The task catches its own failures and reports them in the result, so
      // resolve it and surface the reason rather than relying on a task failure.
      const result = await taskQueueRef.current.waitUntilTaskIsDone(task_id);

      if (!result?.ran) {
        showMessage(
          NotificationAppearance.ERROR,
          result?.reason || "Pre-step failed. Please try again."
        );
      } else if (!result.is_relevant) {
        showMessage(
          NotificationAppearance.WARNING,
          "No web sources matched this product, so no context was added."
        );
      }
    } catch {
      // TaskQueue shows a notification on failure
    } finally {
      setIsRunningPreStep(false);
      await refetchPageData();
    }
  };

  const handleEnrichField = async (field: EnrichedField): Promise<void> => {
    if (!field.customer_field_id) return;

    try {
      const { task_id } = await enrichOne({
        product_id: productId,
        customer_field_id: field.customer_field_id,
        add_reasoning: true,
        // A discarded row's own enrich button means "run it anyway".
        skip_applicability_gate: field.applicability_discarded,
      }).unwrap();

      setEnrichingFieldIds((prev) => new Set(prev).add(field.id));
      taskQueueRef.current.addTaskId(task_id);
      await taskQueueRef.current.waitUntilTaskIsDone(task_id);
    } catch {
      // TaskQueue shows a notification on failure
    } finally {
      // Refetch on both success and failure so a failed run surfaces its
      // "Failure" badge and the manually-editable row.
      refetchEnrichResult();
      setEnrichingFieldIds((prev) => {
        const next = new Set(prev);
        next.delete(field.id);
        return next;
      });
    }
  };

  const getStatusBadgeColor = (status: EnrichStatus): string => {
    switch (status) {
      case EnrichStatus.NOT_ENRICHED:
        return "tw-bg-yellow-100 tw-text-yellow-800 tw-border-yellow-300";
      case EnrichStatus.PARTIALLY_ENRICHED:
        return "tw-bg-amber-100 tw-text-amber-900 tw-border-amber-300";
      case EnrichStatus.ENRICHED:
        return "tw-bg-green-100 tw-text-green-800 tw-border-green-300";
      case EnrichStatus.ENRICHED_AND_SENT_TO_PIM:
        return "tw-bg-blue-100 tw-text-blue-800 tw-border-blue-300";
      case EnrichStatus.FAILED_TO_SEND_TO_PIM:
      case EnrichStatus.ENRICH_FAILED:
        return "tw-bg-red-100 tw-text-red-800 tw-border-red-300";
      default:
        return "tw-bg-gray-100 tw-text-gray-800 tw-border-gray-300";
    }
  };

  const getFieldsetOptions = (): Option<number>[] => {
    if (!pageData?.fieldsets) return [];
    return pageData.fieldsets.map(([id, name]) => ({
      key: String(id),
      value: id,
      text: name,
    }));
  };

  const hasWebIdentificationChanges = (): boolean => {
    if (!pageData) return false;
    const currentEntries = Object.entries(
      pageData.web_identification
    ).map(([key, value]) => [key, String(value ?? "")]);
    const editableEntries = Object.entries(
      editableWebIdentification
    ).map(([key, value]) => [key, String(value ?? "")]);
    return JSON.stringify(currentEntries) !== JSON.stringify(editableEntries);
  };

  const hasSupplierTextChanges = (): boolean => {
    if (!pageData) return false;
    return (pageData.supplier_text || "") !== editableSupplierText;
  };

  const handleSaveWebIdentification = async (): Promise<void> => {
    try {
      await updateEnrichInputOverride({
        product_id: productId,
        web_identification: editableWebIdentification,
      }).unwrap();
    } catch (error) {
      console.error("Failed to save web identification:", error);
      alert("Failed to save web identification. Please try again.");
    }
  };

  const handleSaveSupplierText = async (): Promise<void> => {
    try {
      await updateEnrichInputOverride({
        product_id: productId,
        supplier_text: editableSupplierText,
      }).unwrap();
    } catch (error) {
      console.error("Failed to save supplier text:", error);
      alert("Failed to save supplier text. Please try again.");
    }
  };

  const preStepContexts = pageData?.pre_step_contexts ?? [];

  const relevantContexts = useMemo(
    (): PreStepContext[] => preStepContexts.filter((c) => c.is_relevant),
    [pageData?.pre_step_contexts]
  );

  // Deduped by URL, since a source can appear in more than one search result set.
  const searchedSources = useMemo((): PreStepSource[] => {
    const seen = new Set<string>();
    const out: PreStepSource[] = [];
    for (const context of preStepContexts) {
      for (const source of context.sources ?? []) {
        if (!source.url || seen.has(source.url)) continue;
        seen.add(source.url);
        out.push(source);
      }
    }
    return out;
  }, [pageData?.pre_step_contexts]);

  const searchedQueries = useMemo((): string[] => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const context of preStepContexts) {
      for (const query of context.queries ?? []) {
        if (seen.has(query)) continue;
        seen.add(query);
        out.push(query);
      }
    }
    return out;
  }, [pageData?.pre_step_contexts]);

  const sendErrors = (enrichResults?.filter((r) => r.send_error) ?? [])
    .map((r) => `${r.key}: ${r.send_error}`)
    .join("\n");

  useEffect(() => {
    const imageUrls = (pageData?.images ?? []).map((image) => image.url);
    if (imageUrls.length === 0) {
      if (activeImageUrl !== null) {
        setActiveImageUrl(null);
      }
      return;
    }

    if (!activeImageUrl || !imageUrls.includes(activeImageUrl)) {
      setActiveImageUrl(imageUrls[0]);
    }
  }, [pageData?.images, activeImageUrl]);

  if (isProductLoading || isDataLoading || isResultLoading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-min-h-screen">
        <Spinner />
      </div>
    );
  }

  if (dataError || !pageData) {
    return (
      <div className="tw-p-4 tw-bg-red-100 tw-text-red-700 tw-rounded">
        {dataError ? "Failed to fetch enrich data" : "Failed to load data"}
      </div>
    );
  }

  return (
    <div className="tw-container tw-mx-auto tw-p-6">
      <Modal dialogRef={overwriteWarningDialogRef} size="sm">
        <Modal.Header divide>
          <div className="tw-font-semibold">Overwrite existing PIM value?</div>
        </Modal.Header>
        <Modal.Body minHeight={640}>
          <div className="tw-space-y-3">
            <p className="tw-m-0">
              You are about to send enriched data to PIM for{" "}
              <b>{effectivePendingOverwriteFields.length}</b>{" "}
              {effectivePendingOverwriteFields.length === 1
                ? "field"
                : "fields"}{" "}
              that already{" "}
              {effectivePendingOverwriteFields.length === 1 ? "has" : "have"} a
              value in PIM. This will overwrite the current PIM value. You can
              exclude individual fields below.
            </p>
            {pendingOverwriteFields.length > 0 ? (
              <div className="tw-max-h-128 tw-overflow-auto tw-rounded tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-3">
                <ul className="tw-m-0 tw-p-0 tw-list-none tw-space-y-2">
                  {pendingOverwriteFields.map((f) => (
                    <li
                      key={f.id}
                      className="tw-flex tw-items-start tw-justify-between tw-gap-3"
                    >
                      <div className="tw-min-w-0">
                        <div className="tw-font-medium tw-truncate">
                          {f.display_key}
                        </div>
                        {hasEnrichPimValue(f.existing_pim_value) ? (
                          <div className="tw-text-gray-600 tw-text-sm tw-break-words">
                            Current: {String(f.existing_pim_value)}
                          </div>
                        ) : null}
                      </div>
                      <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-700 tw-whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={excludedPendingSendIds.has(f.id)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setExcludedPendingSendIds((prev) => {
                              const next = new Set(prev);
                              if (checked) {
                                next.add(f.id);
                              } else {
                                next.delete(f.id);
                              }
                              return next;
                            });
                            if (f.customer_field_id != null) {
                              setEnrichFieldIds((prev) => {
                                const next = new Set(prev);
                                if (checked) {
                                  next.delete(f.customer_field_id);
                                } else {
                                  next.add(f.customer_field_id);
                                }
                                return next;
                              });
                            }
                          }}
                          className="tw-rounded tw-border-gray-300"
                        />
                        Exclude
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Actions>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <Button
              variant="secondary-alt"
              size="sm"
              compact="very"
              type="button"
              onClick={() => {
                overwriteWarningDialogRef.current?.close();
                setPendingSendFields(null);
                setExcludedPendingSendIds(new Set());
              }}
            >
              Cancel
            </Button>
            <Button
              variant="textual-blue"
              size="sm"
              compact="very"
              type="button"
              onClick={handleConfirmSendOverwrite}
              loading={isSendingToPim}
            >
              Send anyway
            </Button>
          </div>
        </Modal.Actions>
      </Modal>

      <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-4 tw-mb-6 tw-flex tw-justify-between">
        <div>
          <p className="tw-text-gray-500 tw-text-sm tw-mb-0 tw-pb-0">ID</p>
          <p className="tw-mt-0 tw-pt-0">{product.external_id || product.id}</p>
        </div>
        <div>
          <p className="tw-text-gray-500 tw-text-sm tw-mb-0 tw-pb-0">
            Fieldset
          </p>
          <p className="tw-mt-0 tw-pt-0">{pageData?.product_fieldset}</p>
        </div>
        <div>
          <p className="tw-text-gray-500 tw-text-sm tw-mb-0 tw-pb-0">Created</p>
          <p className="tw-mt-0 tw-pt-0">
            {formatTimestamp(product.created_at)}
          </p>
        </div>
        <div>
          <p className="tw-text-gray-500 tw-text-sm tw-mb-0 tw-pb-0">Status</p>
          <span
            className={`tw-px-3 tw-py-1 tw-rounded-full tw-text-sm tw-font-medium tw-border ${getStatusBadgeColor(
              pageData.status
            )} ${
              pageData.status === EnrichStatus.FAILED_TO_SEND_TO_PIM &&
              sendErrors
                ? "tw-cursor-pointer"
                : ""
            }`}
            {...(pageData.status === EnrichStatus.FAILED_TO_SEND_TO_PIM &&
              sendErrors && {
                "data-balloon": `(Click to copy) - ${sendErrors}`,
                "data-balloon-pos": "left",
                "data-balloon-length": "xlarge",
                role: "button",
                tabIndex: 0,
                onClick: (): void => {
                  navigator.clipboard.writeText(sendErrors ?? "");
                  store.dispatch(
                    setDjangoToastOpen({
                      appearance: NotificationAppearance.SUCCESS,
                      content: "Error message copied to clipboard",
                    })
                  );
                },
              })}
          >
            {pageData.status}
          </span>
        </div>
      </div>

      {/* Input for Enrichment Section */}
      <div className="tw-mb-6">
        <div className="tw-flex tw-items-center tw-justify-between">
          <div>
            <p className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-0 tw-pb-0">
              Input for Enrichment
            </p>
            <p className="tw-text-gray-500 tw-text-sm tw-mt-0 tw-pt-0">
              The data that will be sent to the AI to use as a basis for the
              enrichment
            </p>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Button
              variant="secondary-alt"
              size="sm"
              compact="very"
              className="tw-h-8"
              onClick={handleResyncFromPim}
              disabled={isResyncingFromPim}
              data-testid="resync-from-pim-button"
              data-balloon="Sync from PIM to fetch the latest data and images from PIM"
              data-balloon-pos="left"
              data-balloon-length="xlarge"
            >
              <span className="tw-flex tw-items-center tw-gap-2">
                <Icon name="sync" />{" "}
                {isResyncingFromPim ? "Re-syncing..." : "Re-sync from PIM"}
              </span>
            </Button>
            <Button
              variant="secondary-alt"
              size="sm"
              compact="very"
              className="tw-h-8"
              onClick={() => {
                window.location.href = settingsUrl;
              }}
            >
              <span className="tw-flex tw-items-center tw-gap-2">
                <Icon name="settings" />
                Settings
              </span>
            </Button>
          </div>
        </div>

        <div className="tw-grid tw-grid-cols-3 tw-gap-4">
          {/* Web Identification */}
          <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-4">
            <h3 className="tw-text-lg tw-font-semibold tw-mb-3 tw-text-gray-900">
              Web Identification
            </h3>
            {Object.keys(pageData.web_identification).length > 0 ? (
              <div className="tw-space-y-2">
                {Object.entries(pageData.web_identification).map(
                  ([key, value]) => (
                    <div
                      key={key}
                      className="tw-border-b tw-border-gray-200 tw-pb-2 last:tw-border-b-0"
                    >
                      <div className="tw-text-sm tw-font-medium tw-text-gray-600">
                        {key}
                      </div>
                      <input
                        type="text"
                        value={
                          editableWebIdentification[key] ?? String(value ?? "")
                        }
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setEditableWebIdentification((prev) => ({
                            ...prev,
                            [key]: nextValue,
                          }));
                        }}
                        className="tw-w-full tw-text-sm tw-text-gray-900 tw-mt-1 tw-px-2 tw-py-1 tw-border tw-border-gray-300 tw-rounded"
                        disabled={!!activeJobId || isSavingInputOverride}
                      />
                    </div>
                  )
                )}
                <div className="tw-flex tw-justify-end tw-mt-3">
                  <Button
                    variant="textual-blue"
                    size="sm"
                    compact="very"
                    onClick={handleSaveWebIdentification}
                    disabled={
                      !hasWebIdentificationChanges() ||
                      !!activeJobId ||
                      isSavingInputOverride
                    }
                  >
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <p className="tw-text-gray-500 tw-text-sm">
                No web identification fields configured
              </p>
            )}
          </div>

          {/* Product Images */}
          <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-4">
            <h3 className="tw-text-lg tw-font-semibold tw-mb-3 tw-text-gray-900">
              Product Images
            </h3>
            {pageData.images.length > 0 ? (
              <ImageCarousel
                images={pageData.images}
                onDeleteActiveImage={handleDeleteImage}
                onActiveImageChange={setActiveImageUrl}
              />
            ) : (
              <p className="tw-text-gray-500 tw-text-sm">No images available</p>
            )}
            <div className="tw-flex tw-flex-col">
              {additionalImageUrls.map((imageUrl) => (
                <div
                  className="tw-flex tw-flex-row tw-align-middle"
                  key={imageUrl.id}
                >
                  <Icon
                    onClick={() => handleRemoveImageUrl(imageUrl.id)}
                    name="close"
                    className="tw-my-auto tw-cursor-pointer hover:tw-text-red-500"
                    data-balloon="Remove image URL"
                    data-balloon-pos="left"
                    data-balloon-length="large"
                  />
                  <input
                    type="text"
                    value={imageUrl.url}
                    onChange={(e) =>
                      handleImageUrlChange(imageUrl.id, e.target.value)
                    }
                    className="tw-w-full tw-text-sm tw-text-gray-900 tw-mt-1 tw-px-2 tw-py-1 tw-border tw-border-gray-300 tw-rounded"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
              ))}
              <Button
                content={
                  additionalImageUrls.length > 0
                    ? "Add another image URL"
                    : "Add an image URL"
                }
                variant="secondary-alt"
                size="sm"
                compact="very"
                onClick={handleAddImageUrlField}
                className="tw-my-2 tw-w-fit"
                type="button"
              />
            </div>
            <div className="tw-flex tw-justify-between">
              {additionalImageUrls.length > 0 && (
                <Button
                  content="Add image(s)"
                  variant="textual-blue"
                  onClick={handleAddImages}
                  disabled={isAddingImages}
                  className="tw-my-2"
                  type="button"
                />
              )}
            </div>
          </div>

          {/* Supplier Text */}
          <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-4">
            <h3 className="tw-text-lg tw-font-semibold tw-mb-3 tw-text-gray-900">
              Additional information
            </h3>
            <div className="tw-text-sm tw-font-medium tw-text-gray-600">
              {pageData.supplier_text_field_id || "Supplier Text"}
            </div>
            <textarea
              value={editableSupplierText}
              onChange={(event) => setEditableSupplierText(event.target.value)}
              className="tw-w-full tw-text-sm tw-text-gray-900 tw-px-2 tw-py-1 tw-border tw-border-gray-300 tw-rounded"
              placeholder="No supplier text available"
              disabled={!!activeJobId || isSavingInputOverride}
            />
            <div className="tw-flex tw-justify-end tw-mt-3">
              <Button
                variant="textual-blue"
                size="sm"
                compact="very"
                onClick={handleSaveSupplierText}
                disabled={
                  !hasSupplierTextChanges() ||
                  !!activeJobId ||
                  isSavingInputOverride
                }
              >
                Save
              </Button>
            </div>
            <div className="tw-mt-3">
              <h3 className="tw-text-lg tw-font-semibold tw-mb-3 tw-text-gray-900">
                PDF files
              </h3>
              <MultiFileDrop
                onAddFiles={(files): void => void handleAddFiles(files)}
                buttonOnly
              />
              {pageData?.files?.length > 0 && (
                <ul className="tw-mt-2 tw-space-y-1 tw-list-none tw-pl-0">
                  {pageData.files.map((file) => (
                    <li
                      key={file.id}
                      className="tw-flex tw-items-center tw-gap-2 tw-py-1"
                    >
                      <span className="tw-text-sm tw-truncate tw-flex-1">
                        {file.source_url ? (
                          <a
                            href={file.source_url}
                            target="_blank"
                            rel="noreferrer"
                            className="tw-text-blue-600 hover:tw-underline"
                          >
                            {file.name}
                          </a>
                        ) : (
                          file.name
                        )}
                      </span>
                      <Button
                        type="button"
                        variant="secondary-alt"
                        size="sm"
                        compact
                        content="Remove"
                        onClick={(): void => void handleRemoveFile(file.id)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Product Context — what the pre-step gathered, and what enrichment reads */}
      <div className="tw-mb-6 tw-bg-white tw-shadow tw-rounded-lg tw-p-4">
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-mb-3">
          <div>
            <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-0 tw-pb-0">
              Product Context
            </h3>
            <p className="tw-text-sm tw-text-gray-500 tw-mt-0 tw-pt-0">
              Summarized factual information about the product gathered from
              searching the web.
            </p>
          </div>
          <Button
            variant="secondary-alt"
            size="sm"
            compact="very"
            className="tw-h-8 tw-shrink-0"
            onClick={handleRunPreStep}
            // A running enrich runs the pre-step itself, and _pre_step takes a
            // per-product lock, so running it here would just be discarded.
            disabled={isRunningPreStep || !!activeJobId}
            data-testid="run-pre-step-button"
            data-balloon="Search the web for this product, check the results really describe it, and cache the result as enrichment context"
            data-balloon-pos="left"
            data-balloon-length="xlarge"
          >
            <span className="tw-flex tw-items-center tw-gap-2">
              <Icon name="manage_search" />{" "}
              {isRunningPreStep ? "Preparing..." : "Prepare context"}
            </span>
          </Button>
        </div>

        {isRunningPreStep ? (
          <div
            className="tw-flex tw-justify-center tw-items-center tw-py-8"
            data-testid="pre-step-spinner"
          >
            <Spinner />
          </div>
        ) : (
          <>
            {preStepContexts.length === 0 && (
              <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
                No product context yet. Enriching will search per field.
              </p>
            )}

            {preStepContexts.length > 0 && relevantContexts.length === 0 && (
              <div className="tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded tw-p-3">
                <p className="tw-text-sm tw-font-medium tw-text-amber-900 tw-mb-0">
                  No matching web sources found for this product
                </p>
                {preStepContexts[0]?.reason && (
                  <p className="tw-text-sm tw-text-amber-800 tw-mt-1 tw-mb-0 tw-break-words">
                    {preStepContexts[0].reason}
                  </p>
                )}
                <p className="tw-text-xs tw-text-amber-800 tw-mt-1 tw-mb-0">
                  Nothing is added to the enrichment prompt. Enriching will
                  search per field.
                </p>
              </div>
            )}

            {relevantContexts.map((context) => (
              <div key={context.kind} className="tw-space-y-3">
                <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-2">
                  <span className="tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase">
                    Brief
                  </span>
                  <span className="tw-text-xs tw-text-gray-500">
                    prepared {formatTimestamp(context.updated_at)}
                  </span>
                </div>
                <div className="tw-max-h-64 tw-overflow-auto tw-border tw-border-gray-200 tw-rounded tw-p-3 tw-bg-gray-50">
                  <p className="tw-text-sm tw-text-gray-800 tw-whitespace-pre-wrap tw-break-words tw-mb-0">
                    {context.brief}
                  </p>
                </div>
              </div>
            ))}

            {searchedQueries.length > 0 && (
              <div className="tw-text-xs tw-text-gray-500 tw-mt-2">
                <span className="tw-font-semibold tw-text-gray-600">
                  {searchedQueries.length === 1 ? "Query" : "Queries"} (
                  {searchedQueries.length}):
                </span>{" "}
                {searchedQueries.map((query, i) => (
                  <span key={query}>
                    {i > 0 && " | "}
                    <span className="tw-break-words">{query}</span>
                  </span>
                ))}
              </div>
            )}

            {searchedSources.length > 0 && (
              <div className="tw-mt-4">
                <span className="tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase">
                  Sources searched ({searchedSources.length})
                </span>
                <ul className="tw-list-none tw-pl-0 tw-mb-0 tw-mt-1 tw-space-y-1">
                  {searchedSources.map((source) => (
                    <li key={source.url} className="tw-text-sm tw-min-w-0">
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noreferrer"
                        className="tw-text-blue-600 hover:tw-underline tw-break-all"
                      >
                        {source.title || source.url}
                      </a>
                    </li>
                  ))}
                </ul>
                <span className="tw-text-xs tw-text-gray-500 tw-mt-2 tw-mb-0">
                  <i>
                    These are the sources that were retrieved and checked, the
                    brief does not necessarily include information from all of
                    them.
                  </i>
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Job in progress — show progress bar */}
      {activeJobId && (
        <div className="tw-mb-6 tw-bg-white tw-shadow tw-rounded-lg">
          <div className="tw-p-6">
            <div className="tw-mb-4">
              <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900">
                Enriching...
              </h3>
              <p className="tw-text-sm tw-text-gray-500">
                Job ID: {activeJobId}
                {" - "}
                {activeJobDetailUrl ? (
                  <a
                    href={activeJobDetailUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="tw-text-blue-600 hover:tw-underline"
                  >
                    View details
                  </a>
                ) : null}
                {streamIsActive && (
                  <span className="tw-text-xs tw-text-gray-500 tw-animate-pulse tw-ml-2">
                    Checking Status...
                  </span>
                )}
              </p>
            </div>
            <JobProgressbarHandler
              job={enrichingJob}
              fetchingJob={!enrichingJob}
              retryClicked={false}
              onCancelSuccess={() => {
                setActiveJobId(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Enrich result / validation table */}
      <div className="tw-mb-6 tw-bg-white tw-shadow tw-rounded-lg">
        <EnrichFieldsetSection
          fieldsetOptions={getFieldsetOptions()}
          selectedFieldset={selectedFieldset}
          onFieldsetChange={(id) => setSelectedFieldset(id)}
          isValidating={isValidating}
          validationError={validationError}
          disabled={!!activeJobId}
          editInputSchemasUrl={pageData?.input_schema_url}
        />
        <EnrichViewHeader
          hasEnrichResults={!!enrichResults?.length}
          onEnrich={handleEnrich}
          isEnriching={isEnriching}
          isJobActive={!!activeJobId}
          hasSelectedFields={hasSelectedFields}
          selectedFieldset={selectedFieldset}
          onUpdateMetadata={() => void handleUpdateMetadata()}
          isUpdatingMetadata={isUpdatingMetadata}
          onSendToPim={handleSendToPim}
          isSendingToPim={isSendingToPim}
          latestJobUrl={pageData.latest_job_url ?? null}
          hasSelectedSendableFields={selectedEnrichResults.length > 0}
        />
        <EnrichFieldTable
          sortedValidationFields={sortedValidationFields}
          enrichResults={enrichResults}
          liveJobReveal={liveJobReveal}
          enrichingFieldIds={enrichingFieldIds}
          activeJobId={activeJobId}
          enrichFieldIds={enrichFieldIds}
          enrichSelectableFieldIds={enrichSelectableFieldIds}
          onEnrichFieldIdToggle={toggleEnrichFieldId}
          enrichSelectAllChecked={enrichSelectAllChecked}
          enrichSelectAllIndeterminate={enrichSelectAllIndeterminate}
          onToggleAllEnrichFields={toggleAllEnrichFields}
          enrichSelectAllDisabled={
            !!activeJobId || enrichSelectableFieldIds.length === 0
          }
          onSaveField={handleSaveField}
          onRevertField={handleRevertField}
          onEnrichField={handleEnrichField}
          onEditField={setEditingFieldId}
          onCreateField={handleCreateFieldForValidation}
          isCreatingField={isCreatingField}
        />
      </div>
      {editingFieldId && (
        <CustomerFieldEditModal
          customerFieldId={editingFieldId}
          onClose={() => setEditingFieldId(null)}
          onSave={handleEditModalSave}
        />
      )}
    </div>
  );
};
